import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
  useParams,
} from "react-router-dom";
import { Box, CircularProgress, CssBaseline, useMediaQuery, useTheme } from "@mui/material";
import TopNavigation from "./components/TopNavigation";
import AppSidebar from "./components/AppSidebar";
import TagListView from "./views/TagListView";
import BroadcastView from "./views/BroadcastView";
import ContactView from "./views/ContactView";
import AutomationView from "./views/AutomationView";
import DefaultChatbotsView from "./views/DefaultChatbotsView";
import { FlowCanvas } from "./views/FlowCanvas";
import AutomationService from "./services/AutomationService";
import TeamChatView from "./views/TeamChatView";
import CallingView from "./views/CallingView";
import "./App.css";
import { SignInPage } from "./setup/signin";
import { ForgotPasswordPage } from "./setup/forgot-password";
import appConfig from "./shared/js/app-config";
import API_CONFIG from "./config/api.config";
import AdminLayout from "./admin/AdminLayout";
import CheckoutView from "./views/CheckoutView";
import { SignUpPage } from "./setup/signup";
import { Terms } from "./setup/terms";
import { Privacy } from "./setup/privacy";
import PlansView from "./views/PlansView";
import GroupView from "./views/GroupView";
import SettingsView from "./views/SettingsView";
import AiChatbotSettings from "./views/AiChatbotSettingsView";
import MyAccountService from "./services/MyAccountService";
import ApiService from "./services/ApiService";
import ChatSimulationView from "./views/ChatSimulationView";
import DashboardView from "./views/DashboardView";
import { dash } from "./components/dashboard/dashboardTokens";
import TeamMembersView from "./views/TeamMembersView";
import BroadcastReportView from "./views/BroadcastReportView";
import AppointmentOperationsView from "./views/AppointmentOperationsView";
import TicketOperationsView from "./views/TicketOperationsView";
import LeadsView from "./views/LeadsView";
import PipelineStagesView from "./views/PipelineStagesView";
import LeadCategoriesView from "./views/LeadCategoriesView";
import QuotationsView from "./views/QuotationsView";
import QuotationFormView from "./views/QuotationFormView";
import SubscriptionAccessBanner from "./components/SubscriptionAccessBanner";

function Layout() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopSidebarCollapsed, setDesktopSidebarCollapsed] = useState(true);
  const [account, setAccount] = useState(null);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);

  useEffect(() => {
    MyAccountService.fetch()
      .then((model) => setAccount(model))
      .catch(() => setAccount(null));
  }, []);

  const handleLogout = async () => {
    const url = `${API_CONFIG.BASE_URL}/signout`;
    try {
      await fetch(url, { method: "POST", credentials: "include" });
    } catch (e) {}
    window.location.replace("/");
  };

  const isCheckout = location.pathname.startsWith("/checkout");
  const showSidebar = !isCheckout;

  return (
    <Box sx={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      <CssBaseline />
      {showSidebar && (
        <AppSidebar
          mobileOpen={mobileOpen}
          onClose={() => setMobileOpen(false)}
          desktopCollapsed={desktopSidebarCollapsed}
          account={account}
          onOpenProfile={() => setProfileDialogOpen(true)}
          onLogout={handleLogout}
        />
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          bgcolor: dash.pageBg,
        }}
      >
        {showSidebar && (
          <TopNavigation
            showToolbar={isMobile}
            onMenuClick={() => setMobileOpen((prev) => !prev)}
            profileDialogOpen={profileDialogOpen}
            onCloseProfileDialog={() => setProfileDialogOpen(false)}
          />
        )}
        <SubscriptionAccessBanner account={account} />
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            display: "flex",
            flexDirection: "column",
            mt: showSidebar && isMobile ? "64px" : 0,
            bgcolor: dash.pageBg,
          }}
        >
          <Routes>
            <Route path="/dashboard" element={<DashboardView />} />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/contacts" element={<ContactView />} />
            <Route path="/leads" element={<LeadsView defaultView="kanban" />} />
            <Route path="/leads/list" element={<LeadsView defaultView="list" />} />
            <Route path="/leads/pipeline-stages" element={<PipelineStagesView />} />
            <Route path="/leads/categories" element={<LeadCategoriesView />} />
            <Route path="/quotations" element={<QuotationsView />} />
            <Route path="/quotations/new" element={<QuotationFormView />} />
            <Route path="/quotations/:id/edit" element={<QuotationFormView />} />
            <Route path="/tags" element={<TagListView />} />
            <Route path="/broadcast" element={<Navigate to="/broadcast/templates" replace />} />
            <Route path="/broadcast/templates" element={<BroadcastView />} />
            <Route path="/broadcast/messages" element={<BroadcastView />} />
            <Route path="/broadcast/report/:broadcastId" element={<BroadcastReportView />} />
            <Route path="/broadcast/order-automation" element={<Navigate to="/automation/rules" replace />} />
            <Route path="/teamchat" element={<TeamChatView />} />
            <Route path="/calling" element={<CallingView />} />
            <Route path="/team-members" element={<TeamMembersView />} />
            <Route path="/appointments" element={<AppointmentOperationsView />} />
            <Route path="/tickets" element={<TicketOperationsView />} />
            <Route path="/checkout" element={<CheckoutView />} />
            <Route path="/plan" element={<PlansView />} />
            <Route path="/group" element={<GroupView />} />
            <Route path="/settings/*" element={<SettingsView />} />
            <Route path="/ai-chatbot/*" element={<AiChatbotSettings />} />
            <Route path="/chat-simulation" element={<ChatSimulationView />} />
            <Route path="/automation" element={<Navigate to="/automation/rules" replace />} />
            <Route path="/automation/default-chatbots" element={<DefaultChatbotsView />} />
            <Route
              path="/automation/:tab"
              element={
                <AutomationView
                  onNavigateToFlow={(id) => navigate(`/flow/${id}`)}
                />
              }
            />
            <Route path="/flow/preview-default/:defaultId" element={<DefaultChatbotFlowPreview />} />
            <Route path="/flow/:automationId" element={<FlowCanvasWrapper />} />
            <Route path="/flow" element={<FlowCanvasWrapper />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Box>
      </Box>
    </Box>
  );
}

function FlowCanvasWrapper() {
  const id = useParams();
  return <FlowCanvas automationId={id.automationId} />;
}

function DefaultChatbotFlowPreview() {
  const { defaultId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [previewJson, setPreviewJson] = useState(
    () => location.state?.templateJson ?? null
  );
  const [loading, setLoading] = useState(!location.state?.templateJson);

  useEffect(() => {
    if (previewJson) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const row = await AutomationService.getDefaultChatbotById(defaultId);
        if (!cancelled) setPreviewJson(row?.template ?? null);
      } catch (e) {
        if (!cancelled) setPreviewJson(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [defaultId, previewJson]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!previewJson) {
    return (
      <Box sx={{ p: 3 }}>
        Could not load this flow.{" "}
        <button type="button" onClick={() => navigate("/automation/chatbots?view=library")}>
          Go back
        </button>
      </Box>
    );
  }

  return (
    <FlowCanvas
      readOnly
      previewTemplateJson={previewJson}
      defaultChatbotIdForClone={defaultId}
      onBack={() => navigate(location.state?.from || "/automation/chatbots?view=library")}
      onCloned={(newId) => navigate(`/flow/${newId}`)}
    />
  );
}

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [orgId, setOrgId] = useState(null);
  const [store, setStore] = useState([]);
  useEffect(() => {
    async function initializeApp() {
      try {
        // Shopify embedded app SSO — token passed via ?sso_token= in the URL.
        // Must be inside try/catch so any failure still resolves the loading state.
        const params = new URLSearchParams(window.location.search);
        const ssoToken = params.get('sso_token');
        if (ssoToken) {
          ApiService.setAuthToken(ssoToken);
          params.delete('sso_token');
          const clean = window.location.pathname + (params.toString() ? `?${params.toString()}` : '') + window.location.hash;
          window.history.replaceState({}, '', clean);
        }

        const account = await appConfig.fetch();
        setIsLoggedIn(!!account?.getId());
        const type = account?.getType?.();
        setIsAdmin(type === 'admin' || type === 'super_admin');
      } catch (err) {
        console.log("Account fetch failed:", err);
        setIsLoggedIn(false);
      }
    }
    initializeApp();
  }, []);
  if (isLoggedIn === null) {
    // Show nice centered loader
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }
  return (
    <Router>
      <Routes>
        <Route path="/checkout" element={<CheckoutView />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        {isLoggedIn && isAdmin ? (
          <>
            <Route path="/admin/*" element={<AdminLayout />} />
            <Route path="*" element={<Navigate to="/admin/accounts" replace />} />
          </>
        ) : isLoggedIn ? (
          <>
            {/* No org or no services => redirect to create service */}
            {/* {(!orgId || store.length === 0) ? (
              <>
                <Route path="/create-service" element={<CreateService />} />
                <Route path="*" element={<Navigate to="/create-service" replace />} />
              </>
            ) : ( */}
              <>
                <Route path="/*" element={<Layout />} />
              </>
            {/* )} */}
          </>
        ) : (
          <>
            <Route path="/signin" element={<SignInPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/signup" element={<SignUpPage />} />
            {/* <Route path="/create-service" element={<CreateService />} /> */}
            <Route path="*" element={<Navigate to="/signin" replace />} />
          </>
        )}
      </Routes>
    </Router>
  );
}

export default App;
